import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';
import { platformAPI } from '../../../services/platformAPI';

const STATUSES = [
  ['not_started', 'Not started'],
  ['in_progress', 'In progress'],
  ['blocked', 'Blocked'],
  ['ready_for_review', 'Ready for review'],
  ['done', 'Done']
];

const HISTORY_LABELS = {
  created: 'Created',
  assigned: 'Default owner applied',
  reassigned: 'Reassigned',
  status: 'Status',
  due_override: 'Due date kept as entered',
  rescheduled: 'Rescheduled',
  deadline_changed: 'Deadline changed',
  cancelled: 'Cancelled',
  reopened: 'Reopened',
  review_required: 'Marked for review',
  review_acknowledged: 'Review acknowledged'
};

const WorkPackagesPanel = ({ tenderId }) => {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState('');
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => {
    return platformAPI.getWorkPackages(tenderId).then((response) => {
      setData(response.data?.data || null);
    }).catch((error) => {
      setMessage(error?.response?.data?.message || 'Work packages could not be loaded.');
    });
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const draft = (row) => drafts[row._id] || { status: row.status === 'cancelled' ? row.storedStatus : row.status, blockerReason: row.blockerReason || '', reason: '', ownerId: '' };

  const setDraft = (row, patch) => {
    setDrafts((current) => ({ ...current, [row._id]: { ...draft(row), ...patch } }));
  };

  const save = async (row) => {
    setMessage('');
    try {
      const body = draft(row);
      const payload = {
        status: body.status,
        blockerReason: body.blockerReason,
        reason: body.reason
      };
      if (body.ownerId && body.ownerId !== String(row.ownerId || '')) payload.ownerId = body.ownerId;
      if (body.acknowledgeReview) payload.acknowledgeReview = true;
      await platformAPI.updateWorkPackage(tenderId, row._id, payload);
      setDrafts((current) => ({ ...current, [row._id]: undefined }));
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.message || 'This package could not be saved.');
    }
  };

  if (!data) return message ? <Alert variant="warning" className="small mt-2">{message}</Alert> : null;
  if (!data.packages?.length) return null;

  return (
    <Alert variant="light" className="small mt-2">
      <strong>Work packages</strong>
      <div className="mt-1">{data.progressLabel}</div>
      <div className="mt-1 text-muted">{data.scheduleNote}</div>
      <div className="mt-1 text-muted">{data.distinctions?.notEligible} {data.distinctions?.notQualified}</div>
      {message ? <div className="mt-2">{message}</div> : null}
      <Table size="sm" className="mt-2 mb-0">
        <thead>
          <tr>
            <th>Package</th>
            <th>Owner</th>
            <th>Due</th>
            <th>Status</th>
            <th>Blocker</th>
          </tr>
        </thead>
        <tbody>
          {data.packages.map((row) => {
            const form = draft(row);
            return (
              <tr key={row._id}>
                <td>
                  {row.label}
                  {row.teamName ? <div className="text-muted">{row.teamName}</div> : null}
                  {row.reviewRequired ? <div>{row.reviewNote}</div> : null}
                  {row.code === 'costing' ? (
                    <div className="text-muted">
                      {(row.gates || []).map((gate) => `${gate.name}: ${gate.status === 'pending_integration' ? 'pending integration' : gate.status === 'met' ? 'met' : 'not met'}${gate.detail ? `. ${gate.detail}` : ''}`).join(' ')}
                    </div>
                  ) : null}
                </td>
                <td>
                  {row.assignmentRequired ? 'Assignment required' : (row.ownerName || 'Assigned')}
                  {row.reviewerName ? <div className="text-muted">Reviewer {row.reviewerName}</div> : null}
                  {data.canReassign && !row.cancelled ? (
                    <Form.Select className="mt-1" size="sm" value={form.ownerId} onChange={(event) => setDraft(row, { ownerId: event.target.value })}>
                      <option value="">{row.ownerId ? 'Keep current owner' : 'Assignment required'}</option>
                      {(data.assignees || []).filter((person) => (person.packages || []).includes(row.code)).map((person) => (
                        <option key={person._id} value={person._id}>{person.name}</option>
                      ))}
                    </Form.Select>
                  ) : null}
                  {(row.history || []).length ? (
                    <details className="mt-1">
                      <summary>History</summary>
                      {(row.history || []).slice().reverse().slice(0, 6).map((entry, index) => (
                        <div key={`${entry.at || index}-${entry.action}`}>
                          {HISTORY_LABELS[entry.action] || entry.action}
                          {entry.byName ? ` · ${entry.byName}` : ''}
                          {entry.reason ? ` · ${entry.reason}` : ''}
                        </div>
                      ))}
                    </details>
                  ) : null}
                </td>
                <td>
                  {row.dueOn || '—'}
                  {row.timeKnown ? '' : row.dueOn ? ' (date only)' : ''}
                  {row.manualDue ? ' · manual' : ''}
                  {row.overdue ? ` · ${row.overdueAgeDays} calendar day(s) overdue` : ''}
                </td>
                <td>
                  {row.cancelled ? 'Cancelled' : (
                    <Form.Select size="sm" value={form.status} onChange={(event) => setDraft(row, { status: event.target.value })}>
                      {STATUSES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                    </Form.Select>
                  )}
                </td>
                <td>
                  {row.cancelled ? row.blockerReason || '—' : (
                    <>
                      <Form.Control size="sm" value={form.blockerReason} placeholder="Blocker reason" onChange={(event) => setDraft(row, { blockerReason: event.target.value })} />
                      <Form.Control className="mt-1" size="sm" value={form.reason} placeholder="Reason for a change or reassignment" onChange={(event) => setDraft(row, { reason: event.target.value })} />
                      {row.reviewRequired ? (
                        <Form.Check className="mt-1" label="Review acknowledged" checked={Boolean(form.acknowledgeReview)} onChange={(event) => setDraft(row, { acknowledgeReview: event.target.checked })} />
                      ) : null}
                      <Button className="mt-1" size="sm" variant="outline-secondary" onClick={() => save(row)}>Save</Button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Alert>
  );
};

export default WorkPackagesPanel;
